import React, { useState, useEffect } from 'react'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    Box,
} from '@material-ui/core'

import { InternalInvoiceItem } from '../../../models'

interface EditLineItemDialogProps {
    open: boolean
    onClose: () => void
    onEditLineItem: (data: {
        description?: string
        quantity?: string
        unit_price?: string
        subtotal?: string
    }) => Promise<void>
    lineItem: InternalInvoiceItem | null
    isLoading?: boolean
}

export const EditLineItemDialog: React.FC<EditLineItemDialogProps> = ({
    open,
    onClose,
    onEditLineItem,
    lineItem,
    isLoading = false,
}) => {
    const [formData, setFormData] = useState({
        description: '',
        quantity: '',
        unit_price: '',
        subtotal: '',
    })

    const [errors, setErrors] = useState<{ [key: string]: string }>({})

    // Update form data when lineItem changes
    useEffect(() => {
        if (lineItem) {
            setFormData({
                description: lineItem.description || '',
                quantity: lineItem.quantity?.toString() || '',
                unit_price: lineItem.unit_price?.toString() || '',
                subtotal: lineItem.subtotal?.toString() || '',
            })
        }
    }, [lineItem])

    const handleInputChange = (field: string, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }))
        }
    }

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {}

        if (!formData.description.trim()) {
            newErrors.description = 'Description is required'
        }

        // Validate numeric fields
        if (formData.quantity && isNaN(parseFloat(formData.quantity))) {
            newErrors.quantity = 'Quantity must be a valid number'
        }
        if (formData.unit_price && isNaN(parseFloat(formData.unit_price))) {
            newErrors.unit_price = 'Unit price must be a valid number'
        }
        if (formData.subtotal && isNaN(parseFloat(formData.subtotal))) {
            newErrors.subtotal = 'Subtotal must be a valid number'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async () => {
        if (!validateForm()) {
            return
        }

        try {
            await onEditLineItem({
                description: formData.description,
                quantity: formData.quantity || undefined,
                unit_price: formData.unit_price || undefined,
                subtotal: formData.subtotal || undefined,
            })

            // Reset form
            setFormData({
                description: '',
                quantity: '',
                unit_price: '',
                subtotal: '',
            })
            setErrors({})
        } catch (error) {
            console.error('Error editing line item:', error)
        }
    }

    const handleClose = () => {
        setFormData({
            description: '',
            quantity: '',
            unit_price: '',
            subtotal: '',
        })
        setErrors({})
        onClose()
    }

    if (!lineItem) {
        return null
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Line Item</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} style={{ marginTop: 8 }}>
                    {/* Description */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            value={formData.description}
                            onChange={(e) =>
                                handleInputChange('description', e.target.value)
                            }
                            error={!!errors.description}
                            helperText={errors.description}
                            required
                        />
                    </Grid>

                    {/* Quantity */}
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Quantity"
                            type="number"
                            value={formData.quantity}
                            onChange={(e) =>
                                handleInputChange('quantity', e.target.value)
                            }
                            error={!!errors.quantity}
                            helperText={errors.quantity}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>

                    {/* Unit Price */}
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Unit Price"
                            type="number"
                            value={formData.unit_price}
                            onChange={(e) =>
                                handleInputChange('unit_price', e.target.value)
                            }
                            error={!!errors.unit_price}
                            helperText={errors.unit_price}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>

                    {/* Subtotal */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Subtotal"
                            type="number"
                            value={formData.subtotal}
                            onChange={(e) =>
                                handleInputChange('subtotal', e.target.value)
                            }
                            error={!!errors.subtotal}
                            helperText={
                                errors.subtotal ||
                                'Enter subtotal OR quantity + unit price'
                            }
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>

                    {/* Helper Text */}
                    <Grid item xs={12}>
                        <Box mt={1}>
                            <Typography variant="caption" color="textSecondary">
                                <strong>Note:</strong> You can either enter a
                                subtotal directly, or enter both quantity and
                                unit price. If you enter quantity and unit
                                price, the subtotal will be calculated
                                automatically.
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isLoading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    color="primary"
                    variant="contained"
                    disabled={isLoading}
                >
                    {isLoading ? 'Updating...' : 'Update Line Item'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
